// The Bus tab in the staff app (docs/spec/transport.md slice 2): today's runs at this school, then
// one run stop by stop. Every tap is a signed event from this phone (transport:board, :alight,
// :absent, :held) that lands in the school's chain like a pickup, and works offline through the
// outbox. A drop to an adult is chosen from the child's approved adults and checked by the server
// like a pickup; with nobody approved there, the child stays on the bus.
import { LOCATION_DISCLOSURE, useDisclosure } from "../safety/disclosure.tsx";
import { useEffect, useMemo, useState } from "react";
import { Banner, Button, EmptyState, Sheet, Spinner, useToast } from "@vavr/ui";
import { readRoster, type Roster } from "@vavr/client";
import { api, ApiError, cacheGet, cachePut, fullName, type AppPlatform } from "@vavr/screens/shared";
import { useTeacher } from "@vavr/screens/teacher";
import { nextStop, type Action } from "./drive.ts";
import { metres } from "./eta.ts";
import { RunMap } from "./runmap.tsx";
import { clock, phoneClock, Status, type Tone } from "./status.tsx";
import { lineStates, mapStops, StopLine } from "./stopline.tsx";
import { DriveView } from "./drive-view.tsx";

type Status = "waiting" | "on" | "off" | "absent" | "held";
interface Stop { id: string; seq: number; name: string; time: string | null; site_id: string | null; lat: number | null; lng: number | null }
interface Rider {
  site_id: string; child_id: string; first: string; last: string; stop_id: string; meet_required: boolean; status: Status; at: number | null; adult_id: string | null; flags: string[];
  /** Who may meet them now, and any restriction, from their school (the server sends it; a bus company's phone has no roster). */
  adults?: { id: string; name: string; label: string }[];
  restrictions?: { never_release: boolean; name: string | null }[];
}
interface Run { id: string; driver_account_id: string; started_at: number; finished_at: number | null; lat?: number | null; lng?: number | null; heading?: number | null }
/** `replaces`: the usual bus when another stands in today (PMU-232). */
interface BusRoute { id: string; name: string; kind: "am" | "pm" | "trip"; vehicle: string | null; vehicle_plate?: string | null; replaces?: string | null; stops: Stop[]; riders: Rider[]; run: Run | null; shape?: [number, number][] | null; directions?: boolean; /** The way driven so far. */ trail?: [number, number][] }

const NONE: [number, number][] = [];

/**
 * Offline (PMU-270): a run started or finished with no internet waits in the phone's queue. Until
 * it is sent, the phone's saved copy of today's buses carries it, so the run opens (and survives
 * a restart) and every tap is recorded against its id.
 */
async function patchToday(siteId: string, routeId: string, patch: (run: Run | null) => Run | null) {
  const path = `/api/v1/sites/${siteId}/transport/today`;
  const hit = await cacheGet<{ routes: BusRoute[] }>(path);
  if (!hit) return;
  await cachePut(path, { ...hit.data, routes: hit.data.routes.map((r) => (r.id === routeId ? { ...r, run: patch(r.run) } : r)) });
}
const newRunId = () => `run_${Array.from(crypto.getRandomValues(new Uint8Array(14)), (b) => "0123456789abcdefghijklmnopqrstuvwxyz"[b % 36]).join("")}`;
const KIND = { am: "To school", pm: "From school", trip: "Trip" } as const;

// ---------- sharing the bus's location during a run (slice 4) ----------

type Tracking = { tracking: boolean; status: number; fixAt?: number; locationOff?: boolean };
let trackingState: Tracking = { tracking: false, status: 0 };
const trackingSubs = new Set<(t: Tracking) => void>();
const setTracking = (t: Tracking) => { trackingState = t; for (const f of trackingSubs) f(t); };
let webWatch: number | null = null;

/** A fix kept for the run's trail: the bus moved this far, or this long since the last one kept. */
const KEEP_M = 8, KEEP_MS = 15_000, POST_MS = 10_000, QUEUE_MAX = 4000, BATCH = 200;
interface Fix { lat: number; lng: number; accuracy?: number; speed?: number; heading?: number; at: number }
let webQueue: Fix[] = [];
let webPost: ReturnType<typeof setInterval> | null = null;

/**
 * Starts sharing: the Android background service when the app has it, otherwise the browser's
 * location while this screen is open (web, or an iPhone for now). Either way a fix every few
 * seconds while the bus moves goes into the run's trail, sent every 10 s, and kept while offline.
 */
async function startTracking(platform: AppPlatform, runId: string, token: string, label: string) {
  const url = `${platform.apiBase || location.origin}/api/v1/transport/runs/${runId}/position`;
  if (platform.busTracker) {
    await platform.busTracker.start({ url, token, label });
    setTracking({ tracking: true, status: 0 });
    return;
  }
  if (typeof navigator === "undefined" || !navigator.geolocation) return;
  stopWeb();
  let kept: Fix | null = null, latest: Fix | null = null, sentAt = 0, busy = false;
  webWatch = navigator.geolocation.watchPosition((pos) => {
    const c = pos.coords;
    const f: Fix = { lat: c.latitude, lng: c.longitude, accuracy: c.accuracy, speed: c.speed ?? undefined, heading: c.heading != null && !Number.isNaN(c.heading) && (c.speed ?? 0) > 1.5 ? c.heading : undefined, at: pos.timestamp };
    latest = f;
    // The driver sees "updated … ago" from the first fix, not only once a batch has gone.
    if (!trackingState.fixAt || f.at - trackingState.fixAt > 5_000) setTracking({ ...trackingState, fixAt: f.at });
    if (!kept || metres(kept, f) >= Math.max(KEEP_M, Math.min(f.accuracy ?? 0, 30)) || f.at - kept.at >= KEEP_MS) {
      kept = f;
      webQueue.push(f);
      if (webQueue.length > QUEUE_MAX) webQueue.splice(0, webQueue.length - QUEUE_MAX);
    }
  }, () => setTracking({ tracking: false, status: -1 }), { enableHighAccuracy: true, maximumAge: 2000 });
  const post = () => {
    // Nothing new: the newest fix again after a minute, so a run finished elsewhere answers 409.
    // Nothing new for a minute (or no fix at all): an empty post, so a run finished elsewhere answers 409.
    const idle = Date.now() - sentAt >= 60_000;
    const batch = webQueue.length ? webQueue.slice(0, BATCH) : idle && latest ? [latest] : [];
    if ((!batch.length && !idle) || busy) return;
    busy = true;
    fetch(url, { method: "POST", headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` }, body: JSON.stringify({ points: batch }) })
      .then((r) => {
        if (r.ok || r.status === 400) { webQueue = webQueue.filter((x) => !batch.includes(x)); sentAt = Date.now(); }
        setTracking({ tracking: r.ok, status: r.status, fixAt: latest?.at });
        if (r.status === 401 || r.status === 409) stopTracking(platform);
      }, () => setTracking({ ...trackingState, status: -1, fixAt: latest?.at }))
      .finally(() => { busy = false; });
  };
  webPost = setInterval(post, POST_MS);
  setTracking({ tracking: true, status: 0 });
}

function stopWeb() {
  if (webWatch != null && typeof navigator !== "undefined") navigator.geolocation.clearWatch(webWatch);
  if (webPost != null) clearInterval(webPost);
  webWatch = null; webPost = null; webQueue = [];
}

function stopTracking(platform: AppPlatform) {
  void platform.busTracker?.stop();
  stopWeb();
  setTracking({ tracking: false, status: 0 });
}

function useTracking(platform: AppPlatform): Tracking {
  const [t, setT] = useState(trackingState);
  useEffect(() => {
    trackingSubs.add(setT);
    const off = platform.busTracker?.onState((s) => setTracking({ tracking: s.tracking, status: s.status, fixAt: s.fixAt ?? trackingState.fixAt, locationOff: s.locationOff }));
    return () => { trackingSubs.delete(setT); off?.(); };
  }, [platform]);
  return t;
}

export function BusPanel() {
  const { site, version } = useTeacher();
  const [data, setData] = useState<{ routes: BusRoute[] } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState<string | null>(null);
  const [bump, setBump] = useState(0);
  useEffect(() => {
    let live = true;
    api<{ routes: BusRoute[] }>("GET", `/api/v1/sites/${site.id}/transport/today`).then((d) => { if (live) { setData(d); setError(null); } }, (e: ApiError) => live && setError(e.message));
    return () => { live = false; };
  }, [site.id, version, bump]);
  if (error && !data) return <Banner tone="warning" title="The bus list did not load">{error}</Banner>;
  if (!data) return <div className="pmu-center"><Spinner label="Loading today's buses" /></div>;
  const route = data.routes.find((r) => r.id === open);
  if (route) return <RunView route={route} onBack={() => setOpen(null)} onChanged={() => setBump((x) => x + 1)} />;
  if (!data.routes.length) return <EmptyState title="No buses today" description="No route with children from this school runs today." />;
  return (
    <div className="pmt-wide pmt-buslist">
      {data.routes.map((r) => {
        const p = progress(r);
        const line = lineStates(r.stops, r.run ? nextStop(r, r.riders) : null, !!r.run, !!r.run?.finished_at);
        return (
          <section key={r.id} className="pmt-busitem" aria-label={r.name}>
            <div className="pmt-busitem__map" aria-hidden="true">
              <RunMap id={`list-${r.id}`} plan={r.shape} trail={r.trail ?? NONE} stops={mapStops(line)} height="100%" label={`${r.name} on the map`} bus={busOf(r)} />
            </div>
            <div className="pmt-busitem__body">
              <div>
                <h2 className="pmt-card__title">{r.name}</h2>
                <p className="pmu-small pmu-muted">{KIND[r.kind]}{r.vehicle ? ` · ${r.vehicle}${r.replaces ? ` (for ${r.replaces} today)` : ""}` : ""} · {r.riders.length} {r.riders.length === 1 ? "child" : "children"} · {r.stops.length} stops</p>
              </div>
              <Status tone={p.tone} live={p.live}>{p.text}</Status>
              {r.run && !r.run.finished_at && <div className="pmt-progress" role="progressbar" aria-label="Stops done" aria-valuemin={0} aria-valuemax={p.total} aria-valuenow={p.done}><span style={{ width: `${(p.done / Math.max(1, p.total)) * 100}%` }} /></div>}
              <StartButton route={r} onOpen={() => setOpen(r.id)} onChanged={() => setBump((x) => x + 1)} />
            </div>
          </section>
        );
      })}
    </div>
  );
}

/** The bus on a map while its run is on the road and it has reported where it is. */
const busOf = (r: BusRoute) => (r.run && !r.run.finished_at && r.run.lat != null && r.run.lng != null ? { lat: r.run.lat, lng: r.run.lng, heading: r.run.heading ?? null, title: "The bus" } : null);

/** Where a run is: not started (and when), on the road (how many on board, the next stop), or finished. */
function progress(r: BusRoute): { text: string; tone: Tone; live: boolean; done: number; total: number } {
  const stops = [...r.stops].sort((a, b) => a.seq - b.seq);
  const next = nextStop(r, r.riders);
  const at = next ? stops.findIndex((s) => s.id === next.id) : stops.length;
  const on = r.riders.filter((x) => x.status === "on" || x.status === "held").length;
  if (r.run?.finished_at) return { text: `Finished at ${phoneClock(r.run.finished_at)}`, tone: "success", live: false, done: stops.length, total: stops.length };
  if (r.run) return { text: next ? `On the road · ${on} on board · next ${next.name}` : `On the road · ${on} on board · all stops done`, tone: "info", live: true, done: at, total: stops.length };
  const first = stops[0]?.time ? clock(stops[0].time) : null;
  return { text: first ? `Not started · first stop ${first}` : "Not started", tone: "neutral", live: false, done: 0, total: stops.length };
}

function StartButton({ route, onOpen, onChanged }: { route: BusRoute; onOpen: () => void; onChanged: () => void }) {
  const { site, device, platform, me } = useTeacher();
  const [busy, setBusy] = useState(false);
  const toast = useToast();
  const disclose = useDisclosure(LOCATION_DISCLOSURE);
  if (route.run?.finished_at) return <Button fullWidth variant="secondary" onClick={onOpen}>See the run</Button>;
  // Starting, or continuing on this phone, also (re)starts sharing the bus's location: said first (PMU-238).
  const go = async () => {
    if (!(await disclose.ask())) return;
    setBusy(true);
    try {
      // The phone names a new run itself, so it can start with the internet down (PMU-270).
      const id = route.run?.id ?? newRunId(), startedAt = Date.now();
      const r = await api<{ id: string; tracking: { token: string } | null; queued?: boolean }>("POST", `/api/v1/sites/${site.id}/transport/runs`, { route_id: route.id, id, started_at: startedAt },
        { queue: { label: `Start of ${route.name}`, result: { id, tracking: null, queued: true } } });
      if (r.queued) {
        await patchToday(site.id, route.id, (run) => run ?? { id, driver_account_id: me.id, started_at: startedAt, finished_at: null });
        toast.show({ title: "Run started offline", description: "Taps are saved on this phone. Location sharing starts when you tap Continue driving with the internet back.", tone: "info" });
      } else await startTracking(platform, r.id, r.tracking!.token, route.name).catch((e: Error) => toast.show({ title: e.message, tone: "warning" }));
      onChanged(); onOpen();
    } catch (e) { toast.show({ title: (e as ApiError).message, tone: "danger" }); }
    finally { setBusy(false); }
  };
  return <>{disclose.dialog}<Button fullWidth icon={device ? "bus" : undefined} loading={busy} disabled={!device} onClick={go}>{!device ? "Link this phone to start" : route.run ? "Continue driving" : "Start the run"}</Button></>;
}

function TrackingLine() {
  const { platform } = useTeacher();
  const t = useTracking(platform);
  const [now, setNow] = useState(Date.now());
  useEffect(() => { const i = setInterval(() => setNow(Date.now()), 5000); return () => clearInterval(i); }, []);
  if (!t.tracking) return <Banner tone="warning" title="Not sharing the bus's location">Parents see planned times only. Continue the run from the list to share it.</Banner>;
  if (t.locationOff) return <Banner tone="warning" title="Turn on Location">This phone's location is off, so parents cannot see the bus coming. Turn it on in the phone's quick settings.</Banner>;
  // A fix stamped by the phone's GPS can be a moment ahead of its clock: never "−1 s ago".
  const ago = t.fixAt ? Math.max(0, Math.round((now - t.fixAt) / 1000)) : null;
  return <p className="pmu-small pmu-muted pmt-tracking"><span className="pmt-tracking__dot" aria-hidden="true" />Sharing location{ago != null ? ` · updated ${ago < 60 ? `${ago} s` : `${Math.round(ago / 60)} min`} ago` : " · waiting for GPS"}{t.status > 299 ? " · not reaching the server" : ""}</p>;
}

/** Which stop a rider is dealt with at now: their home stop, or the school. */
const schoolStop = (r: BusRoute) => r.stops.find((s) => s.site_id) ?? null;

function RunView({ route, onBack, onChanged }: { route: BusRoute; onBack: () => void; onChanged: () => void }) {
  const { site, record, device, db, platform } = useTeacher();
  const toast = useToast();
  // Taps show at once; the server's view (from the sealed events) replaces them when it arrives.
  const [local, setLocal] = useState<Record<string, Partial<Rider>>>({});
  const [handTo, setHandTo] = useState<Rider | null>(null);
  const [roster, setRoster] = useState<Roster | null>(null);
  const [finishing, setFinishing] = useState<{ children: string[] } | null>(null);
  const [driving, setDriving] = useState(false);
  useEffect(() => { if (device) readRoster(db, device.mirrorKey).then(setRoster, () => setRoster(null)); }, [db, device]);
  // The server's view replaces a tap only once it is at least as new: offline, the reloaded list is
  // the saved copy without the tap, which must not undo it on screen (PMU-270).
  useEffect(() => {
    setLocal((l) => Object.fromEntries(Object.entries(l).filter(([child, x]) => {
      const server = route.riders.find((r) => r.child_id === child);
      return !server || (server.at ?? 0) < (x.at ?? 0);
    })));
  }, [route]);
  const riders = route.riders.map((x) => ({ ...x, ...local[x.child_id] }));
  const run = route.run;
  const school = schoolStop(route);
  const finished = !!run?.finished_at;

  const act = async (x: Rider, type: "board" | "alight" | "absent" | "held", stop: Stop, extra: { adultId?: string; handover?: boolean; atSchool?: boolean } = {}) => {
    if (!run) return;
    const status: Status = type === "board" ? "on" : type === "alight" ? "off" : type;
    setLocal((l) => ({ ...l, [x.child_id]: { status, at: Date.now() } }));
    try {
      await record({
        kind: "module", type: `transport:${type}`, childId: x.child_id, siteId: x.site_id, adultId: extra.adultId ?? null, authMethod: extra.adultId ? "tile" : "none",
        data: { run_id: run.id, stop_id: stop.id, ...(extra.handover ? { handover: true } : {}), ...(extra.atSchool ? { at_school: true } : {}) },
      });
    } catch (e) {
      setLocal((l) => { const n = { ...l }; delete n[x.child_id]; return n; });
      toast.show({ title: (e as Error).message, tone: "danger" });
    }
  };

  const finish = async (checked: boolean) => {
    try {
      const r = await api<{ queued?: boolean }>("POST", `/api/v1/sites/${site.id}/transport/runs/${run!.id}/finish`, { checked_bus: checked }, { queue: { label: `Finish of ${route.name}` } });
      if (r?.queued) await patchToday(site.id, route.id, (x) => (x ? { ...x, finished_at: Date.now() } : x));
      toast.show({ title: r?.queued ? "Run finished (sent when the internet is back)" : "Run finished", tone: "success" });
      stopTracking(platform);
      setFinishing(null); onChanged(); onBack();
    } catch (e) {
      const err = e as ApiError;
      if (err.code === "riders_on_board") setFinishing({ children: (err.data?.children as string[] | undefined) ?? riders.filter((r) => r.status === "on" || r.status === "held").map(fullName) });
      else toast.show({ title: err.message, tone: "danger" });
    }
  };

  const aboard = riders.filter((r) => r.status === "on" || r.status === "held");
  const off = riders.filter((r) => r.status === "off").length;
  const next = run && !finished ? nextStop(route, riders) : null;
  const sheets = (
    <>
      <Sheet title={handTo ? `Hand ${handTo.first} to` : ""} open={handTo !== null} onOpenChange={(o) => !o && setHandTo(null)}>
        {handTo && <AdultPicker roster={roster} rider={handTo} onPick={(adultId) => { const stop = route.stops.find((s) => s.id === handTo.stop_id)!; void act(handTo, "alight", stop, { adultId, handover: true }); setHandTo(null); }}
          onNobody={() => { const stop = route.stops.find((s) => s.id === handTo.stop_id)!; void act(handTo, "held", stop); setHandTo(null); }} />}
      </Sheet>
      <Sheet title="Children still on the bus" open={finishing !== null} onOpenChange={(o) => !o && setFinishing(null)}>
        {finishing && (
          <div className="pmu-stack">
            <Banner tone="danger" title="Walk the bus before you finish">{finishing.children.join(", ")} {finishing.children.length === 1 ? "is" : "are"} still marked on the bus. Check every seat. Take any child still on board to the school office.</Banner>
            <Button variant="danger" onClick={() => finish(true)}>I checked every seat: finish</Button>
            <Button variant="ghost" onClick={() => setFinishing(null)}>Go back</Button>
          </div>
        )}
      </Sheet>
    </>
  );
  // A tap from the stop line or the drive screen: at school in the afternoon "off" means off at school; a handover picks the adult first.
  const doAct = (x: Rider, a: Action | "absent", at: { id: string }) => {
    const stop = route.stops.find((s) => s.id === at.id)!;
    void (a === "alight-school" ? act(x, "alight", school ?? stop, { atSchool: true }) : a === "handover" ? setHandTo(x) : act(x, a, stop));
  };
  if (driving && run && !finished) {
    return (
      <>
        <DriveView route={route} runId={run.id} riders={riders} onList={() => setDriving(false)} onFinish={() => finish(false)} onHandTo={setHandTo} onAct={doAct} />
        {sheets}
      </>
    );
  }
  const line = lineStates(route.stops, next, !!run, finished);
  return (
    <div className="pmt-wide pmt-run">
      <div className="pmt-run__back"><Button variant="ghost" icon="chevron-left" onClick={onBack}>All buses</Button></div>
      <header className="pmt-run__head">
        <h2 className="pmt-card__title">{route.name}</h2>
        <p className="pmu-small pmu-muted">{KIND[route.kind]}{route.vehicle ? ` · ${route.vehicle}${route.vehicle_plate ? ` (${route.vehicle_plate})` : ""}${route.replaces ? `, standing in for ${route.replaces}` : ""}` : ""} · {riders.length} {riders.length === 1 ? "child" : "children"}</p>
        {finished ? <Status tone="success">{`Finished at ${phoneClock(run!.finished_at!)}`}</Status> : run && <TrackingLine />}
      </header>
      <div className="pmt-run__map">
        <RunMap id={`run-${route.id}`} plan={route.shape} trail={route.trail ?? NONE} stops={mapStops(line)} height="100%" label={`${route.name} on the map`} bus={busOf(route)} follow />
      </div>
      <div className="pmt-run__side">
        {run && (
          <div className="pmt-run__bar">
            <dl className="pmt-runsum">
              <div><dt>On board</dt><dd>{aboard.length}</dd></div>
              <div><dt>Off</dt><dd>{off}</dd></div>
            </dl>
            {!finished && route.directions !== false && <Button size="lg" icon="bus" onClick={() => setDriving(true)}>Drive</Button>}
          </div>
        )}
        <StopLine route={route} stops={route.stops} riders={riders} next={next} started={!!run} finished={finished} onAct={doAct} onHandTo={setHandTo} />
        {!finished && run && <Button fullWidth variant="secondary" onClick={() => finish(false)}>Finish the run</Button>}
      </div>
      {sheets}
    </div>
  );
}

/** The child's approved adults now (guardians, authorised, today's delegates), and any restriction. */
function AdultPicker({ roster, rider, onPick, onNobody }: { roster: Roster | null; rider: Rider; onPick: (adultId: string) => void; onNobody: () => void }) {
  const now = Date.now();
  // From the server with today's list; else this phone's copy of its own school's roster.
  const fromRoster = useMemo(() => {
    if (!roster) return null;
    const byId = new Map(roster.adults.map((a) => [a.id, a]));
    const name = (id: string) => { const a = byId.get(id); return a ? fullName(a) : null; };
    const adults = [
      ...roster.links.filter((l) => l.child_id === rider.child_id && (l.valid_from == null || l.valid_from <= now) && (l.valid_to == null || l.valid_to >= now)).map((l) => ({ id: l.adult_id, name: name(l.adult_id), label: l.relation })),
      ...roster.delegations.filter((d) => d.child_ids.includes(rider.child_id) && d.valid_from <= now && d.valid_to >= now).map((d) => ({ id: d.delegate_adult_id, name: name(d.delegate_adult_id), label: "Today only" })),
    ].filter((x): x is { id: string; name: string; label: string } => !!x.name);
    const restrictions = roster.restrictions.filter((r) => r.child_id === rider.child_id).map((r) => ({ never_release: r.never_release, name: r.subject_name ?? (r.subject_adult_id ? name(r.subject_adult_id) : null) }));
    return { adults, restrictions };
  }, [roster, rider.child_id, now]);
  const adults = rider.adults ?? fromRoster?.adults ?? null;
  const restrictions = rider.restrictions ?? fromRoster?.restrictions ?? [];
  if (!adults) return <Banner tone="warning" title="The approved adults are not on this phone yet">Connect once so the phone can load them.</Banner>;
  return (
    <div className="pmu-stack">
      {restrictions.map((r, i) => <Banner key={i} tone="restriction" title={r.never_release ? "Never release to" : "Restriction"}>{r.name ?? "See the office"}</Banner>)}
      <p className="pmu-muted">Only hand {rider.first} to someone on this list.</p>
      {adults.map((a) => (
        <Button key={`${a.id}-${a.label}`} variant="secondary" fullWidth onClick={() => onPick(a.id)}>{a.name}{a.label ? ` · ${a.label}` : ""}</Button>
      ))}
      <Button variant="danger" fullWidth onClick={onNobody}>Nobody approved is here: keep {rider.first} on the bus</Button>
    </div>
  );
}
